import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import '../Login/Login.css';
import './Signup.css';
import AuthCard from '../AuthCard/AuthCard';
import { useAuth } from '../../contexts/AuthContext';
import { PASSWORD_POLICY_MESSAGE, passwordPolicyError } from '../../utils/passwordPolicy';
import { BOT_CHECK_UNAVAILABLE_MESSAGE, LEGAL_LINKS } from '../../constants';
import { getTurnstileSiteKey } from '../../utils/amplifyConfig';
import ConfirmSignupForm from './ConfirmSignupForm';
import TurnstileWidget from './TurnstileWidget';

type SignupStep = 'form' | 'confirm' | 'verified';

const Signup: React.FC = () => {
  const { registerAccount, resendRegistrationCode, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState<SignupStep>('form');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });
  // Task 05. Recorded after auto sign-in; see registerAccount in AuthContext.
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  // The email exactly as sent to registerAccount, so confirm uses the identical
  // string: Amplify matches the auto sign-in state to it with a strict !==.
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  // Signup bot check. No site key means no widget, no token and a signUp call
  // unchanged from before the check existed.
  const siteKey = getTurnstileSiteKey();
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  // Bumped to remount the widget: a token is single-use.
  const [widgetKey, setWidgetKey] = useState(0);
  // The widget failed. The form then submits without a token and the server
  // decides, so turning the check off works even where Cloudflare can't load.
  const [botCheckFailed, setBotCheckFailed] = useState(false);

  const resetBotCheck = () => {
    setTurnstileToken(null);
    setBotCheckFailed(false);
    setWidgetKey((k) => k + 1);
  };

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/reports');
    }
  }, [isAuthenticated, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setNotice('');

    if (formData.password !== formData.confirmPassword) {
      setError("Passwords don't match.");
      return;
    }

    const policyError = passwordPolicyError(formData.password);
    if (policyError) {
      setError(policyError);
      return;
    }

    const normalisedEmail = formData.email.trim().toLowerCase();
    setIsLoading(true);

    try {
      await registerAccount(normalisedEmail, formData.password, acceptedTerms, turnstileToken);
      setEmail(normalisedEmail);
      setStep('confirm');
    } catch (err) {
      // signUp spent the token whatever it answered, including on an existing
      // email, so the next submit needs a fresh challenge.
      if (siteKey) resetBotCheck();
      if (err instanceof Error && err.name === 'UsernameExistsException') {
        // Back, or a reload before confirming. If the account is still
        // unconfirmed a resend succeeds and the user can finish; if it is
        // confirmed the resend fails and the "sign in instead" copy stands.
        // SignUp already disclosed that the account exists, so this reveals
        // nothing new.
        try {
          await resendRegistrationCode(normalisedEmail);
          setEmail(normalisedEmail);
          setNotice("You've already started signing up with this email. We've sent a new code.");
          setStep('confirm');
        } catch {
          setError(err.message);
        }
      } else {
        setError(err instanceof Error ? err.message : 'Unable to create your account. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmed = (outcome: 'SIGNED_IN' | 'CONFIRMED') => {
    setNotice('');
    if (outcome === 'SIGNED_IN') {
      navigate('/reports');
    } else {
      setStep('verified');
    }
  };

  const handleBack = () => {
    setError('');
    setNotice('');
    // The form remounts the widget fresh; the old token was spent.
    setTurnstileToken(null);
    setBotCheckFailed(false);
    setStep('form');
  };

  const canSubmit = !isLoading && !!formData.email && !!formData.password && !!formData.confirmPassword && acceptedTerms
    && (!siteKey || !!turnstileToken || botCheckFailed);

  const title =
    step === 'confirm' ? 'Verify your email' :
    step === 'verified' ? 'Email verified' :
    'Create your PropWise account';
  const subtitle =
    step === 'confirm' ? `We've sent a code to ${email}` :
    step === 'verified' ? 'Sign in to get started.' :
    'Free to join. Buy inspection runs when you need them.';

  return (
    <AuthCard title={title} subtitle={subtitle}>
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {notice && (
        <div className="alert alert-info" role="status">
          {notice}
        </div>
      )}

      {step === 'confirm' ? (
        <ConfirmSignupForm email={email} onConfirmed={handleConfirmed} onCancel={handleBack} />
      ) : step === 'verified' ? (
        <div className="login-form">
          <div className="alert alert-success" role="status">
            Your email is verified. Sign in with your email and password.
          </div>
          <Link to="/login" className="btn btn-primary btn-login">Sign in</Link>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <div className="form-floating">
              <input
                type="email"
                className="form-control"
                id="signupEmail"
                name="email"
                placeholder="name@example.com"
                value={formData.email}
                onChange={handleChange}
                autoComplete="email"
                required
              />
              <label htmlFor="signupEmail">Email address</label>
            </div>
          </div>

          <div className="form-group">
            <div className="form-floating">
              <input
                type="password"
                className="form-control"
                id="signupPassword"
                name="password"
                placeholder="Password"
                value={formData.password}
                onChange={handleChange}
                autoComplete="new-password"
                aria-describedby="signupPasswordHint"
                required
              />
              <label htmlFor="signupPassword">Password</label>
            </div>
            <div id="signupPasswordHint" className="signup-password-hint">
              {PASSWORD_POLICY_MESSAGE}
            </div>
          </div>

          <div className="form-group">
            <div className="form-floating">
              <input
                type="password"
                className="form-control"
                id="signupConfirmPassword"
                name="confirmPassword"
                placeholder="Confirm Password"
                value={formData.confirmPassword}
                onChange={handleChange}
                autoComplete="new-password"
                required
              />
              <label htmlFor="signupConfirmPassword">Confirm Password</label>
            </div>
          </div>

          <div className="form-group">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="signupAcceptTerms"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                required
              />
              <label className="form-check-label" htmlFor="signupAcceptTerms">
                I agree to the{' '}
                <a href={LEGAL_LINKS[0].href} target="_blank" rel="noopener noreferrer">{LEGAL_LINKS[0].label}</a>
                {' '}and{' '}
                <a href={LEGAL_LINKS[1].href} target="_blank" rel="noopener noreferrer">{LEGAL_LINKS[1].label}</a>
              </label>
            </div>
          </div>

          {siteKey && (
            <TurnstileWidget
              key={widgetKey}
              siteKey={siteKey}
              onToken={(token) => {
                setTurnstileToken(token);
                setBotCheckFailed(false);
                // Turnstile retries on its own; drop the failure copy once it passes.
                setError((current) => (current === BOT_CHECK_UNAVAILABLE_MESSAGE ? '' : current));
              }}
              onExpire={() => setTurnstileToken(null)}
              onError={() => {
                setTurnstileToken(null);
                setBotCheckFailed(true);
                // Through the one alert: a second role="alert" would be ambiguous.
                setError(BOT_CHECK_UNAVAILABLE_MESSAGE);
              }}
            />
          )}

          <button
            type="submit"
            className="btn btn-primary btn-login"
            disabled={!canSubmit}
          >
            {isLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Creating account...
              </>
            ) : (
              'Create Account'
            )}
          </button>
        </form>
      )}

      {step === 'form' && (
        <div className="login-footer">
          <div className="login-links">
            <span>Already have an account?</span>
            <Link to="/login" className="login-link">Sign in</Link>
          </div>
        </div>
      )}
    </AuthCard>
  );
};

export default Signup;
